/**
 * Live preview of the embeddable widget.
 *
 * Two modes:
 *
 * - `mock` renders a faithful static reproduction from the settings currently
 *   in the editor, so colours and copy update as they are typed, before
 *   anything is saved.
 * - `live` embeds the real `widget.js` in a sandboxed iframe, so what is shown
 *   is the actual shipped widget talking to the actual backend. It reflects
 *   *saved* settings only, which is exactly what makes it worth having beside
 *   the mock.
 *
 * The iframe is sandboxed to scripts and same-origin: the widget needs both to
 * run and to reach the API, and nothing else.
 */

import { useMemo, type ReactNode } from 'react';

import type { WidgetConfig } from '../api/types';

/** What the widget shows when a bot has no footer text of its own. */
export const DEFAULT_BRANDING_TEXT = 'Powered by FormaChat';

export type PreviewView = 'conversation' | 'teaser' | 'fullpage';

export interface WidgetColors {
  header: string;
  headerText: string;
  launcher: string;
  background: string;
  ai: string;
  aiText: string;
  user: string;
  userText: string;
  button: string;
  buttonText: string;
  link: string;
}

/**
 * The colours the widget actually draws with. Mirrors `buildStylesheet` in
 * widget/src/styles.js: an unset colour follows the one it always has, so
 * keep the two in step.
 */
export function widgetColors(widget: WidgetConfig): WidgetColors {
  return {
    header: widget.header_color,
    headerText: widget.header_text_color ?? '#ffffff',
    launcher: widget.launcher_color ?? widget.header_color,
    background: widget.background_color ?? '#ffffff',
    ai: widget.ai_message_color,
    aiText: widget.ai_text_color ?? '#1a1a1a',
    user: widget.user_message_color,
    userText: widget.user_text_color ?? '#ffffff',
    button: widget.button_color ?? widget.user_message_color,
    buttonText: widget.button_text_color ?? '#ffffff',
    link: widget.link_color ?? widget.user_message_color,
  };
}

interface MockProps {
  widget: WidgetConfig;
  botName: string;
  greeting: string;
  suggestions: string[];
  view: PreviewView;
}

export function WidgetMockPreview({
  widget,
  botName,
  greeting,
  suggestions,
  view,
}: MockProps): JSX.Element {
  const colors = widgetColors(widget);
  const frameClass =
    view === 'fullpage'
      ? 'w-full h-[30rem]'
      : 'w-[22rem] h-[28rem] ml-auto mr-0 shadow-xl';

  if (view === 'teaser') {
    return (
      <div className="flex h-[28rem] flex-col items-end justify-end gap-3 p-4">
        {widget.dismissible_notice && (
          <div className="max-w-[16rem] rounded-lg bg-white px-3 py-2 text-sm text-slate-700 shadow-lg">
            {widget.dismissible_notice}
          </div>
        )}
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full shadow-lg"
          style={{ backgroundColor: colors.launcher, color: colors.headerText }}
          aria-hidden
        >
          💬
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-xl border border-slate-200 ${frameClass}`}
      style={{ backgroundColor: colors.background }}
    >
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ backgroundColor: colors.header, color: colors.headerText }}
      >
        {widget.header_logo_url && (
          <img src={widget.header_logo_url} alt="" className="h-5 w-auto" />
        )}
        <span className="text-sm font-semibold">{botName}</span>
        <span className="ml-auto opacity-70">✕</span>
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
        {widget.brand_logo_url && (
          <img
            src={widget.brand_logo_url}
            alt=""
            className="mb-1 max-h-10 max-w-[60%] self-center"
          />
        )}
        <AiBubble widget={widget} colors={colors}>
          {greeting || `Hi, I'm the ${botName} assistant.`}
        </AiBubble>
        <div
          className="max-w-[85%] self-end rounded-xl px-3 py-2 text-sm"
          style={{ backgroundColor: colors.user, color: colors.userText }}
        >
          Do you build custom workbenches?
        </div>
        <AiBubble widget={widget} colors={colors}>
          Yes — every bench is built to order. See{' '}
          <span className="underline" style={{ color: colors.link }}>
            formaspace.com/workbenches
          </span>
        </AiBubble>
      </div>

      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pb-2">
          {suggestions.slice(0, 3).map((item) => (
            <span
              key={item}
              className="rounded-full border px-2.5 py-1 text-xs"
              style={{ borderColor: colors.button, color: colors.button }}
            >
              {item}
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2 border-t border-slate-200 p-3">
        <div className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-400">
          Type your message…
        </div>
        <div
          className="rounded-md px-4 py-2 text-sm font-semibold"
          style={{ backgroundColor: colors.button, color: colors.buttonText }}
        >
          Send
        </div>
      </div>

      {widget.show_branding && (
        <div className="pb-2 text-center text-[11px] text-slate-400">
          {widget.branding_url ? (
            <a
              href={widget.branding_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              {widget.branding_text || DEFAULT_BRANDING_TEXT}
            </a>
          ) : (
            widget.branding_text || DEFAULT_BRANDING_TEXT
          )}
        </div>
      )}
    </div>
  );
}

/** An assistant bubble, beside the avatar when one is set — as the widget draws it. */
function AiBubble({
  widget,
  colors,
  children,
}: {
  widget: WidgetConfig;
  colors: WidgetColors;
  children: ReactNode;
}): JSX.Element {
  const bubble = (
    <div
      className="max-w-[85%] self-start rounded-xl px-3 py-2 text-sm"
      style={{ backgroundColor: colors.ai, color: colors.aiText }}
    >
      {children}
    </div>
  );
  if (!widget.avatar_url) return bubble;
  return (
    <div className="flex max-w-[92%] items-end gap-1.5 self-start">
      <img
        src={widget.avatar_url}
        alt=""
        className="h-7 w-7 flex-none rounded-full object-cover"
      />
      {bubble}
    </div>
  );
}

export function WidgetLivePreview({ slug }: { slug: string }): JSX.Element {
  // Keyed on the slug so switching bots remounts the iframe rather than
  // leaving the previous bot's widget running inside it.
  const srcDoc = useMemo(
    () => `<!doctype html>
<html><head><meta charset="utf-8">
<style>
  body { margin:0; font-family: system-ui, sans-serif; background:#f8fafc; height:100vh; }
  .hint { padding:16px; color:#64748b; font-size:13px; }
</style></head>
<body>
<p class="hint">Live widget — this is the real widget.js talking to the real backend.
Replies here are genuine and are stored in the Chat Inbox.</p>
<script src="/widget.js" data-bot="${slug}"></script>
</body></html>`,
    [slug],
  );

  return (
    <iframe
      key={slug}
      title="Live widget preview"
      srcDoc={srcDoc}
      sandbox="allow-scripts allow-same-origin"
      className="h-[34rem] w-full rounded-xl border border-slate-200 bg-white"
    />
  );
}
